import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Phone, Star, ShieldCheck } from "lucide-react";
import { ASSETS, BUSINESS, SERVICES, AREAS, GALLERY } from "@/data/site";
import { QuoteButton, CallButton } from "@/components/Buttons";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { TrustBar } from "@/components/TrustBar";
import { BrandsMarquee } from "@/components/BrandsMarquee";
import { QuoteForm } from "@/components/QuoteForm";
import { ProcessSection } from "@/components/ProcessSection";
import { Testimonials } from "@/components/Testimonials";
import { HomeFAQ } from "@/components/HomeFAQ";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const HeroHeadline = () => {
  const words = [["Electrical,", "Heating &"], ["Cooling", "Specialists"]];
  let idx = 0;
  return (
    <h1 className="font-display text-[2.6rem] leading-[1.02] sm:text-6xl lg:text-7xl font-bold tracking-tight text-white">
      {[["Electrical, Heating &"], ["Cooling Specialists"]].map((grp, r) => (
        <span key={r} className="reveal-line">
          <motion.span variants={line} custom={idx++} initial="hidden" animate="show" className="block">
            {grp[0]}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

const Manifesto = () => {
  const chapters = [
    { n: "01", t: "Genuine local service", d: "A real, established team serving Victorian and NSW communities — not a faceless call centre." },
    { n: "02", t: "Qualified workmanship", d: "CEC certified, a registered plumber with the VBA, and access to a qualified A-Grade Electrician." },
    { n: "03", t: "Energy-smart solutions", d: "From reverse-cycle systems to heat pump hot water, we help you run more efficiently." },
  ];
  return (
    <section className="bg-white py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <SectionLabel>Why Access</SectionLabel>
          <h2 className="mt-5 max-w-3xl font-display text-3xl md:text-5xl font-bold tracking-tight text-brand-blue text-balance">
            Trades you can trust, delivered with genuine care.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 md:grid-cols-3">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div className="h-full bg-white p-8 md:p-10">
                <div className="font-display text-5xl font-bold text-slate-200">{c.n}</div>
                <h3 className="mt-6 font-display text-xl font-semibold text-brand-blue">{c.t}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Services = () => (
  <section className="bg-[#FAFAFA] py-20 md:py-32">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <Reveal>
          <SectionLabel>Our Expertise</SectionLabel>
          <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-5xl font-bold tracking-tight text-brand-blue text-balance">
            Electrical, air conditioning & hot water — done right.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/services" data-testid="home-all-services" className="inline-flex items-center gap-2 font-medium text-brand-electric hover:gap-3 transition-all">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = Icons[s.icon] || Icons.Wrench;
          return (
            <Reveal key={s.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/95 backdrop-blur">
                    <Icon className="h-5 w-5 text-brand-electric" strokeWidth={1.75} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-xl font-semibold text-brand-blue">{s.title}</h3>
                  <p className="mt-2 flex-1 text-[15px] leading-relaxed text-slate-600">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-electric">
                    Learn more <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden bg-brand-blue">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img src={ASSETS.vanHero} alt="Access Electrical and Air Conditioning service van at a residential property" className="h-full w-full object-cover object-center" />
        </motion.div>
        <div
          className="absolute inset-0 md:hidden"
          style={{ background: "linear-gradient(to top, #0A192F 8%, rgba(10,25,47,0.85) 45%, rgba(10,25,47,0.45) 100%)" }}
        />
        <div
          className="absolute inset-0 hidden md:block"
          style={{ background: "linear-gradient(to right, #0A192F 0%, rgba(10,25,47,0.96) 40%, rgba(10,25,47,0.55) 62%, rgba(10,25,47,0) 85%)" }}
        />

        <div className="relative mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-end px-5 pb-16 pt-28 lg:px-8 lg:justify-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm text-white backdrop-blur">
              <Star className="h-4 w-4 text-brand-gold" fill="currentColor" /> {BUSINESS.yearsInBusiness} years serving VIC &amp; NSW
            </span>
          </motion.div>

          <div className="mt-6 max-w-3xl">
            <HeroHeadline />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 max-w-xl text-lg text-slate-200 leading-relaxed"
          >
            Professional air conditioning, electrical and energy-efficient upgrade solutions backed by {BUSINESS.yearsInBusiness} years of service and {BUSINESS.combinedExperience} years of combined industry experience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <QuoteButton testId="hero-quote" />
            <CallButton dark testId="hero-call" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300"
          >
            {BUSINESS.credentials.slice(0, 3).map((c) => (
              <span key={c} className="inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-brand-gold" /> {c}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      <TrustBar />
      <Manifesto />
      <Services />

      {/* Brands */}
      <section className="bg-white py-16 border-y border-slate-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="text-center text-sm uppercase tracking-[0.2em] text-[#486581] font-semibold">Brands we work with</p>
          </Reveal>
        </div>
        <div className="mt-10">
          <BrandsMarquee />
        </div>
      </section>

      <ProcessSection />


      {/* Recent installations */}
      <section className="bg-[#FAFAFA] py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <SectionLabel>Recent Installations</SectionLabel>
              <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-5xl font-bold tracking-tight text-brand-blue text-balance">
                Real projects. Proven results.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/gallery" data-testid="home-gallery-link" className="inline-flex items-center gap-2 font-medium text-brand-electric hover:gap-3 transition-all">
                View full gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {GALLERY.slice(0, 4).map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="overflow-hidden rounded-2xl border border-slate-200 group">
                  <img src={g.src} alt={g.alt} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <HomeFAQ />

      {/* Quote section */}
      <section className="bg-white py-20 md:py-32" id="quote">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <SectionLabel>Get in touch</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-brand-blue text-balance">
              Request your free quote
            </h2>
            <p className="mt-5 max-w-md text-lg text-slate-600 leading-relaxed">
              Tell us about your project and our team will get back to you shortly with honest, upfront advice.
            </p>
            <div className="mt-8 space-y-4">
              <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-brand-blue font-medium hover:text-brand-electric transition-colors">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-blue/5"><Phone className="h-5 w-5 text-brand-electric" /></span>
                {BUSINESS.phone}
              </a>
              <div className="grid gap-3 pt-2">
                {AREAS.vic.slice(0, 4).concat(AREAS.nsw.slice(0, 1)).map((a) => (
                  <span key={a} className="text-sm text-slate-500">• {a}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-[#FAFAFA] p-6 md:p-8">
              <QuoteForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
